/**
 * Terms and Conditions Form Page (Create/Edit)
 */
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { PageHeader, RichTextEditor } from '../../components/common';
import {
  useGetTermsAdminDetailQuery,
  useCreateTermsMutation,
  useUpdateTermsMutation,
} from '../../store/api/termsApi';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const TermsFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: termsData, isLoading: isLoadingTerms } = useGetTermsAdminDetailQuery(Number(id!), {
    skip: !isEdit,
  });

  const [createTerms, { isLoading: isCreating }] = useCreateTermsMutation();
  const [updateTerms, { isLoading: isUpdating }] = useUpdateTermsMutation();

  const [title, setTitle] = useState('');
  const [terms, setTerms] = useState('');

  useEffect(() => {
    if (isEdit && termsData?.data) {
      setTitle(termsData.data.title);
      setTerms(termsData.data.terms || '');
    }
  }, [isEdit, termsData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error(t('terms.toast.titleRequired'));
      return;
    }
    try {
      if (isEdit) {
        await updateTerms({ id: Number(id!), title: title.trim(), terms }).unwrap();
        toast.success(t('terms.toast.updateSuccess'));
      } else {
        await createTerms({ title: title.trim(), terms }).unwrap();
        toast.success(t('terms.toast.createSuccess'));
      }
      navigate('/terms');
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      toast.error(e?.data?.message || t('terms.toast.saveError'));
    }
  };

  if (isEdit && isLoadingTerms) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={isEdit ? t('terms.form.editTitle') : t('terms.form.createTitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.terms'), path: '/terms' },
          { label: isEdit ? t('terms.breadcrumb.edit') : t('terms.breadcrumb.create') },
        ]}
      />

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/terms')} sx={{ mb: 3 }}>
        {t('terms.buttons.backToTerms')}
      </Button>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {t('terms.form.title')}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <TextField
                fullWidth
                label={t('terms.form.title')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder={t('terms.form.titlePlaceholder')}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {t('terms.form.content')}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <RichTextEditor
                label={t('terms.form.contentLabel')}
                value={terms}
                onChange={setTerms}
                placeholder={t('terms.form.contentPlaceholder')}
                minHeight={320}
                helperText={t('terms.form.contentHelper')}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/terms')}
                  disabled={isCreating || isUpdating}
                  size="large"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isCreating || isUpdating || !title.trim()}
                  size="large"
                >
                  {isCreating || isUpdating ? t('terms.buttons.saving') : isEdit ? t('common.update') : t('common.create')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </form>
    </Box>
  );
};

export default TermsFormPage;
